'use client';

import type {ReactNode} from 'react';
import {Suspense} from 'react';
import React, {useEffect} from 'react';
import clsx from 'clsx';

import type {EventsFilterSearchParams} from '../../app/(frontend)/kalender/page';
import type {SIMPLEVIEW_EVENT_CALENDAR_FRONTPAGE_QUERY_RESULT} from '../../schemas/types';
import {ImageFill} from '../image/ImageFill';
import {imageFromSanityImage} from '../image/query';
import {useHero} from '../page-head/context/page-head-context';

import {Filter} from './filter';

type BaseProps = Pick<
	NonNullable<SIMPLEVIEW_EVENT_CALENDAR_FRONTPAGE_QUERY_RESULT>,
	'heading' | 'tagline' | 'description' | 'image'
>;

export type SimpleviewCategory = {
	label: string;
	value: string;
};

export type SimpleviewMunicipality = {
	label: string;
	value: string;
};

type Props = BaseProps & {
	categories?: SimpleviewCategory[];
	municipalities?: SimpleviewMunicipality[];
	filters?: EventsFilterSearchParams;
};

export const Hero = ({
	tagline,
	heading,
	description,
	image,
	categories,
	municipalities,
	filters,
}: Props) => {
	const {setDarkHero} = useHero();

	useEffect(() => {
		setDarkHero(true);

		// Reset the context when the component unmounts
		return () => setDarkHero(false);
	}, [setDarkHero]);

	const imageWeb = imageFromSanityImage(image);

	return (
		<div className={clsx('relative', 'w-full', 'mb-16', 'overflow-x-clip')}>
			<div
				className={clsx(
					'absolute',
					'-top-28',
					'lg:-top-44',
					'w-screen',
					'left-[calc(50%-50vw)]',
					'bottom-0',
					'-z-20',
					'bg-forestGreen-500',
					'after:absolute',
					'after:bg-black/30',
					'after:w-full',
					'after:h-full',
					'after:top-0',
					'after:left-0',
				)}
			>
				{imageWeb && <ImageFill {...imageWeb} alt="" />}
			</div>
			<div
				className={clsx(
					'w-full',
					'max-w-screen-xl',
					'mx-auto',
					'px-6',
					'h-full',
					'grid',
					'items-center',
					'md:grid-cols-2',
					'lg:grid-cols-3',
					'z-50',
					'sm:py-24',
					'py-12',
					'gap-8',
					'overflow-hidden',
				)}
			>
				<div
					className={clsx(
						'flex',
						'flex-col',
						'gap-y-4',
						'md:gap-y-12',
						'gap-x-24',
						'text-beige-500',
						'md:col-span-1',
						'lg:col-span-2',
					)}
				>
					<div
						className={clsx(
							'text-xs',
							'font-medium',
							'sm:text-pMedium',
							'col-start-1',
						)}
					>
						{tagline}
					</div>
					<h1
						className={clsx(
							'text-h3',
							'sm:text-h1',
							'col-start-1',
							'w-full',
						)}
					>
						{heading}
					</h1>
					<div
						className={clsx(
							'text-xs',
							'font-medium',
							'sm:text-pMedium',
							'col-start-1',
						)}
					>
						{description}
					</div>
				</div>
				<div className={clsx('w-full')}>
					<Suspense fallback={null}>
						<Filter
							filters={filters}
							categories={categories}
							municipalities={municipalities}
						/>
					</Suspense>
				</div>
			</div>
		</div>
	);
};

type SelectProps = {
	children: ReactNode;
	label: string;
	name: string;
	id: string;
	value?: string;
	darkLabel?: boolean;
};

export const EventPageSelect = ({
	children,
	label,
	id,
	name,
	value,
	darkLabel,
}: SelectProps) => {
	return (
		<div>
			<label
				htmlFor={id}
				className={clsx(
					darkLabel ? 'text-forestGreen-700' : 'text-beige-500',
					'font-semibold',
					'ml-4',
					'mb-1',
					'block',
				)}
			>
				{label}
			</label>
			<select
				defaultValue={value}
				id={id}
				name={name}
				className={clsx(
					'select-base',
					'w-full',
					'active:text-forestGreen-700',
					'bg-beige-500',
					'h-12',
					'lg:h-14',
					'px-6',
					'text-bodyMedium',
					'outline-none',
					'rounded-full',
					'border-forestGreen-700',
					'focus:border-forestGreen-700',
					'focus:ring-forestGreen-700',
					'focus:ring-2',
					'focus:outline-none',
					'focus:ring-offset-2',
					'transition-all',
					'duration-200',
				)}
			>
				{children}
			</select>
		</div>
	);
};
